import { useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useQuery } from '@tanstack/react-query';
import { useSeoMeta } from '@unhead/react';
import { ArrowLeft, FolderPlus, Images, KeyRound, Loader2, Lock, Share2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoginArea } from '@/components/auth/LoginArea';
import { AlbumGallery } from '@/components/AlbumGallery';
import { AlbumSharePanel } from '@/components/AlbumSharePanel';
import { AlbumUploader } from '@/components/AlbumUploader';
import { AddSubAlbumDialog } from '@/components/AddSubAlbumDialog';
import { useAlbum } from '@/hooks/useAlbums';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  ALBUM_KIND,
  albumHue,
  albumLink,
  keyFromHash,
  loadAlbumKey,
  saveAlbumKey,
  type AlbumManifest,
  type SubAlbumRef,
} from '@/lib/album';
import { b64uToBytes, deriveAlbumKey } from '@/lib/albumCrypto';

function useDerivedKey(masterKey: string | undefined, salt: string | undefined, d: string | undefined) {
  return useQuery({
    queryKey: ['album-key', masterKey, salt, d],
    queryFn: () => deriveAlbumKey(b64uToBytes(masterKey!), salt!, d!),
    enabled: Boolean(masterKey && salt && d),
    staleTime: Infinity,
  });
}

export default function AlbumPage() {
  useSeoMeta({ title: 'Locket Album', description: 'A private, end-to-end encrypted photo & video album.' });

  const { naddr } = useParams();
  const location = useLocation();

  const decoded = useMemo(() => {
    try {
      const d = nip19.decode(naddr ?? '');
      return d.type === 'naddr' && d.data.kind === ALBUM_KIND ? d.data : null;
    } catch {
      return null;
    }
  }, [naddr]);

  const { user } = useCurrentUser();

  const immediateKey = useMemo(() => {
    const fromHash = keyFromHash(location.hash);
    if (fromHash) return fromHash;
    if (decoded) return loadAlbumKey(decoded.pubkey, decoded.identifier);
    return undefined;
  }, [location.hash, decoded]);

  const album = useAlbum(decoded?.pubkey, decoded?.identifier);

  // Owner recovery: the master key is backed up on the manifest, NIP-44-encrypted
  // to the owner, so logging in on any device unlocks the album.
  const recovery = useQuery({
    queryKey: ['album-key-recovery', decoded?.pubkey, decoded?.identifier, album.data?.event.id],
    queryFn: async () => {
      const plain = await user!.signer.nip44!.decrypt(user!.pubkey, album.data!.encryptedKey!);
      saveAlbumKey(decoded!.pubkey, decoded!.identifier, plain);
      return plain;
    },
    enabled: Boolean(
      !immediateKey &&
        album.data?.encryptedKey &&
        user &&
        user.pubkey === decoded?.pubkey &&
        user.signer.nip44,
    ),
    staleTime: Infinity,
    retry: false,
  });

  const masterKey = immediateKey ?? recovery.data;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2 font-semibold">
            <Lock className="size-4" /> Locket Album
          </Link>
          <LoginArea className="max-w-40" />
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-8">
        {!decoded ? (
          <Message icon={<Lock className="size-8" />} title="Invalid album link" body="This link does not point to a Locket album." />
        ) : album.isPending ? (
          <AlbumSkeleton />
        ) : album.isError ? (
          <Message icon={<Loader2 className="size-8" />} title="Album not found" body="The album could not be loaded from the relays. It may not exist, or the relays are slow — try again in a moment." />
        ) : !masterKey ? (
          recovery.isPending ? (
            <Message
              icon={<Loader2 className="size-8 animate-spin" />}
              title="Unlocking album…"
              body="Decrypting the album key with your Nostr identity."
            />
          ) : (
            <Message
              icon={<KeyRound className="size-8" />}
              title="This album is locked"
              body="The decryption key is missing from the link. Ask the album owner for the full link including the part after # — or log in with the owner's Nostr account to unlock it automatically."
            />
          )
        ) : (
          <AlbumView manifest={album.data} masterKey={masterKey} ownerPubkey={decoded.pubkey} />
        )}
      </main>
      <Footer />
    </div>
  );
}

function AlbumView({
  manifest,
  masterKey,
  ownerPubkey,
}: {
  manifest: AlbumManifest;
  masterKey: string;
  ownerPubkey: string;
}) {
  const { user } = useCurrentUser();
  const isOwner = user?.pubkey === ownerPubkey;
  const [shareOpen, setShareOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const keyQuery = useDerivedKey(masterKey, manifest.salt, manifest.d);
  const link = albumLink(window.location.origin, ownerPubkey, manifest.d, masterKey);
  const totalFiles = manifest.files.length;

  return (
    <div className="space-y-6">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> All albums
      </Link>

      {manifest.cover && (
        <div className="overflow-hidden rounded-2xl border">
          <img src={manifest.cover} alt="Album cover" className="h-44 w-full object-cover sm:h-56" />
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold sm:text-3xl">{manifest.title || 'Untitled album'}</h1>
          <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Images className="size-4" />
            {totalFiles} {totalFiles === 1 ? 'file' : 'files'}
            {manifest.subs.length > 0 && ` · ${manifest.subs.length} sub-album${manifest.subs.length === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isOwner && <AlbumUploader manifest={manifest} masterKeyB64u={masterKey} />}
          {isOwner && (
            <Button variant="outline" onClick={() => setSubOpen(true)}>
              <FolderPlus className="size-4" /> Sub-album
            </Button>
          )}
          <Button variant="outline" onClick={() => setShareOpen(true)}>
            <Share2 className="size-4" /> Share
          </Button>
        </div>
      </div>

      {keyQuery.isPending ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Preparing decryption key…
        </div>
      ) : manifest.subs.length === 0 ? (
        <AlbumGallery files={manifest.files} albumKey={keyQuery.data!} />
      ) : (
        <Tabs defaultValue="main">
          <TabsList className="flex-wrap">
            <TabsTrigger value="main">{manifest.title || 'Main album'}</TabsTrigger>
            {manifest.subs.map((s) => (
              <TabsTrigger key={s.d} value={s.d}>
                {s.name}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="main" className="pt-4">
            <AlbumGallery files={manifest.files} albumKey={keyQuery.data!} />
          </TabsContent>
          {manifest.subs.map((s) => (
            <TabsContent key={s.d} value={s.d} className="pt-4">
              <SubAlbumView ownerPubkey={ownerPubkey} sub={s} masterKey={masterKey} isOwner={isOwner} />
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share album</DialogTitle>
            <DialogDescription>
              Only people with this exact link can decrypt and view the album.
            </DialogDescription>
          </DialogHeader>
          <AlbumSharePanel
            link={link}
            name={manifest.title}
            albumId={manifest.d}
            coverUrl={manifest.cover}
            hue={albumHue(manifest.d)}
          />
        </DialogContent>
      </Dialog>

      {isOwner && <AddSubAlbumDialog parent={manifest} open={subOpen} onOpenChange={setSubOpen} />}
    </div>
  );
}

function SubAlbumView({
  ownerPubkey,
  sub,
  masterKey,
  isOwner,
}: {
  ownerPubkey: string;
  sub: SubAlbumRef;
  masterKey: string;
  isOwner: boolean;
}) {
  const album = useAlbum(ownerPubkey, sub.d);
  const keyQuery = useDerivedKey(masterKey, album.data?.salt, sub.d);

  if (album.isPending || keyQuery.isPending) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square rounded-xl" />
        ))}
      </div>
    );
  }

  if (album.isError) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 px-8 text-center">
          <p className="text-muted-foreground">Sub-album could not be loaded.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {isOwner && <AlbumUploader manifest={album.data} masterKeyB64u={masterKey} />}
      <AlbumGallery files={album.data.files} albumKey={keyQuery.data!} />
    </div>
  );
}

function Message({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <Card className="border-dashed">
      <CardContent className="py-16 px-8 text-center">
        <div className="mx-auto mb-4 w-fit text-muted-foreground">{icon}</div>
        <h2 className="text-xl font-semibold">{title}</h2>
        <p className="mt-2 text-muted-foreground max-w-md mx-auto">{body}</p>
      </CardContent>
    </Card>
  );
}

function AlbumSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-44 w-full rounded-2xl" />
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square rounded-xl" />
        ))}
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t py-6">
      <p className="text-center text-xs text-muted-foreground">
        <a href="https://marlowe.shakespeare.wtf" target="_blank" rel="noreferrer" className="hover:underline">
          Vibed with Marlowe
        </a>
      </p>
    </footer>
  );
}
